/* ============================================================================
   PARHELIK — global motion & interaction engine (framework-free)
   Wires data-ph-* hooks: parallax, reveal, count-up, scroll-explode, colorways.
   Re-scans on Shopify theme-editor section events. Respects reduced-motion.
   ========================================================================== */
(function () {
  'use strict';
  window.__phMotion = true; // failsafe flag: theme.liquid reveals content if we never ran
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var io = null;

  /* ---------- reveal + count-up via IntersectionObserver ---------- */
  function ensureObserver() {
    if (io || !('IntersectionObserver' in window)) return;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        if (el.hasAttribute('data-ph-reveal')) el.classList.add('is-in');
        if (el.hasAttribute('data-ph-count')) runCount(el);
        io.unobserve(el);
      });
    }, { threshold: 0.18 });
  }

  function observeAll(root) {
    root = root || document;
    if (!io) { // no IO support: just show everything
      root.querySelectorAll('[data-ph-reveal]').forEach(function (el) { el.classList.add('is-in'); });
      root.querySelectorAll('[data-ph-count]').forEach(function (el) { el.textContent = formatCount(el, parseFloat(el.getAttribute('data-to')) || 0); });
      return;
    }
    root.querySelectorAll('[data-ph-reveal],[data-ph-count]').forEach(function (el) {
      if (el.dataset.phSeen === '1') return;
      el.dataset.phSeen = '1';
      if (reduce) { // honour reduced motion: reveal instantly, set final count
        if (el.hasAttribute('data-ph-reveal')) el.classList.add('is-in');
        if (el.hasAttribute('data-ph-count')) el.textContent = formatCount(el, parseFloat(el.getAttribute('data-to')) || 0);
        return;
      }
      io.observe(el);
    });
  }

  function formatCount(el, v) {
    return el.getAttribute('data-comma') === '1' ? Math.round(v).toLocaleString('en-US') : Math.round(v).toString();
  }
  function runCount(el) {
    var to = parseFloat(el.getAttribute('data-to')) || 0;
    var dur = parseFloat(el.getAttribute('data-dur')) || 1500;
    var start = null;
    function step(now) {
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / dur);
      p = 1 - Math.pow(1 - p, 3);
      el.textContent = formatCount(el, to * p);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* ---------- scroll: parallax + exploded anatomy ---------- */
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { handleScroll(); ticking = false; });
  }
  function handleScroll() {
    if (reduce) return;
    var y = window.scrollY || window.pageYOffset || 0;
    document.querySelectorAll('[data-ph-parallax]').forEach(function (el) {
      var sp = parseFloat(el.getAttribute('data-ph-parallax')) || 0;
      el.style.transform = 'translateY(' + (y * sp) + 'px)';
    });
    document.querySelectorAll('[data-ph-explode]').forEach(function (sec) {
      var r = sec.getBoundingClientRect();
      var total = sec.offsetHeight - window.innerHeight;
      var p = total > 0 ? (-r.top) / total : 0;
      p = Math.max(0, Math.min(1, p));
      sec.querySelectorAll('[data-ph-part]').forEach(function (el) {
        var m = parseFloat(el.getAttribute('data-spread')) || 0;
        el.style.transform = 'translateX(-50%) translateY(' + (m * p) + 'px)';
      });
      sec.querySelectorAll('[data-ph-plabel]').forEach(function (el) {
        el.style.opacity = Math.max(0, Math.min(1, (p - 0.12) / 0.45)).toString();
        el.style.transform = 'translateX(' + ((1 - p) * 16) + 'px)';
      });
      var pct = sec.querySelector('[data-ph-explode-pct]');
      if (pct) pct.textContent = Math.round(p * 100).toString().padStart(2, '0');
    });
  }

  /* ---------- colorway switching ---------- */
  var COLOR_LABELS = { sand: 'Sandstone', graphite: 'Graphite', pearl: 'Pearl', rose: 'Rose Quartz' };
  function setColorway(cw, scope) {
    scope = scope || document;
    document.querySelectorAll('[data-ph-color]').forEach(function (b) {
      var on = b.getAttribute('data-ph-color') === cw;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-ph-model]').forEach(function (m) { recolorModel(m, cw); });
    document.querySelectorAll('[data-ph-color-label]').forEach(function (l) { l.textContent = COLOR_LABELS[cw] || cw; });
    document.documentElement.setAttribute('data-ph-active-color', cw);
  }
  function recolorModel(model, cw) {
    // The toothbrush snippet is driven by --tb-* CSS vars keyed off [data-cw].
    // Setting the attribute on the model root recolors it via CSS (single-DOM).
    model.setAttribute('data-cw', cw);
  }
  function onColorClick(e) {
    var btn = e.target.closest('[data-ph-color]');
    if (!btn) return;
    setColorway(btn.getAttribute('data-ph-color'));
  }

  /* ---------- init / re-scan ---------- */
  function init(root) {
    ensureObserver();
    observeAll(root);
    handleScroll();
    // restore active colorway after section reload
    var active = document.documentElement.getAttribute('data-ph-active-color');
    if (active) setColorway(active);
  }

  function boot() {
    ensureObserver();
    observeAll(document);
    handleScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('click', onColorClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify theme editor: re-wire when sections load/select */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function () { handleScroll(); });

  /* expose for sections that need to trigger programmatically */
  window.Parhelik = { observeAll: observeAll, setColorway: setColorway, handleScroll: handleScroll };
})();
